import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import api from '@/services/api';
import {
  Robot,
  Buildings,
  ChartBar,
  Users,
  Gear,
  ListBullets,
  ChartLine,
  Pulse,
  ShieldCheck,
  Presentation,
  CreditCard,
  Sparkle,
  Flask,
  Megaphone,
  Lock,
} from '@phosphor-icons/react';
import { TooltipProvider } from '@/components/ui/tooltip';
import AdminSidebar from '@/components/layout/admin/AdminSidebar';
import { APP_VERSION } from '@/appVersion';
import AdminTopBar from '@/components/layout/admin/AdminTopBar';

const adminNavGroups = [
  { label: null, items: [{ path: '/admin', icon: ChartBar, label: 'Control Tower', exact: true }] },
  {
    label: 'Management',
    items: [
      { path: '/admin/dealerships', icon: Buildings, label: 'Dealerships', addonBadge: true },
      { path: '/admin/users', icon: Users, label: 'Users' },
      { path: '/admin/billing', icon: CreditCard, label: 'Billing' },
      { path: '/admin/onboarding', icon: Sparkle, label: 'Onboarding' },
    ],
  },
  {
    label: 'AI & Agents',
    items: [
      { path: '/admin/agents', icon: Robot, label: 'AI Agents' },
      { path: '/admin/approvals', icon: ShieldCheck, label: 'AI Approvals' },
      { path: '/admin/agent-analytics', icon: Pulse, label: 'Agent Analytics' },
      { path: '/admin/ab-testing', icon: Flask, label: 'A/B Testing' },
    ],
  },
  {
    label: 'Growth',
    items: [
      { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
      { path: '/admin/audit-leads', icon: Megaphone, label: 'Audit Leads', auditBadge: true },
      { path: '/admin/demos', icon: Presentation, label: 'Demos' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { path: '/admin/features', icon: ListBullets, label: 'Features' },
      { path: '/admin/settings', icon: Gear, label: 'Settings' },
      { path: '/admin/security', icon: Lock, label: 'Security' },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [auditNewCount, setAuditNewCount] = useState(0);
  const [addonPendingCount, setAddonPendingCount] = useState(0);
  const [dismissedAuditCount, setDismissedAuditCount] = useState(0);
  const [dismissedAddonCount, setDismissedAddonCount] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const saved = localStorage.getItem('adminSidebarCollapsed');
    if (saved) setSidebarCollapsed(JSON.parse(saved));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/audit-leads/new-count');
        setAuditNewCount(res.data?.count || 0);
      } catch {
        setAuditNewCount(0);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/addons/summary');
        setAddonPendingCount(res.data?.pending_request_count || 0);
      } catch {
        setAddonPendingCount(0);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const initials = user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'A';
  const handleLogout = () => { logout(); navigate('/login'); };
  const toggleCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(next));
  };
  const hasUnreadNotifications = auditNewCount > dismissedAuditCount || addonPendingCount > dismissedAddonCount;
  const onMarkAllRead = async () => {
    try {
      await api.post('/admin/audit-leads/mark-seen');
      setAuditNewCount(0);
    } catch {}
    setDismissedAuditCount(auditNewCount);
    setDismissedAddonCount(addonPendingCount);
  };

  return (
    <TooltipProvider>
      <div className="h-screen flex bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
        <AdminSidebar
          adminNavGroups={adminNavGroups}
          sidebarCollapsed={sidebarCollapsed}
          toggleCollapse={toggleCollapse}
          addonPendingCount={addonPendingCount}
          auditNewCount={auditNewCount}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <div className="flex-1 flex flex-col min-w-0">
          <AdminTopBar
            setSidebarOpen={setSidebarOpen}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            auditNewCount={auditNewCount}
            addonPendingCount={addonPendingCount}
            hasUnreadNotifications={hasUnreadNotifications}
            navigate={navigate}
            onMarkAllRead={onMarkAllRead}
            initials={initials}
            user={user}
            handleLogout={handleLogout}
          />
          <main className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950 flex flex-col min-h-0">
            <div className="flex-1 min-h-0">
              <Outlet />
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2 border-t border-slate-200 dark:border-slate-800 shrink-0" data-testid="app-version-footer">
              LotMate v{APP_VERSION}
            </p>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
